import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Snackbar, Alert } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AudioDownloader = ({ filename, onReset }) => {
  const downloadUrl = `${API_URL}/download/${filename}`;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isSharing, setIsSharing] = useState(false);

  const handleShare = async () => {
    setIsSharing(true);

    try {
      const response = await axios.post(`${API_URL}/share/`, null, {
        params: {
          filename,
        },
      });

      if (response.status === 201) {
        setSnackbarMessage(
          <>
            Added to the{' '}
            <a href="/feed" style={{ color: 'inherit' }}>
              public feed
            </a>
            !
          </>
        );
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.data.message || 'Shared with unknown status');
        setSnackbarSeverity(response.data.severity || 'info');
      }
      setSnackbarOpen(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sharing audio:', error);
      setSnackbarMessage(
        error.response?.data?.message || 'Failed to share audio. Please try again.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 4,
          width: '100%',
          maxWidth: 500,
          textAlign: 'center',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Your edit is ready...
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Things will get better
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            component="a"
            href={downloadUrl}
            download
          >
            Download Audio
          </Button>
          <Button variant="outlined" color="secondary" onClick={onReset}>
            Process Another File
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <audio controls style={{ width: '100%' }}>
            <source src={downloadUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </Box>

        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<StarIcon />}
            onClick={handleShare}
            disabled={isSharing}
          >
            {isSharing ? 'Sharing...' : 'Add to Public Feed'}
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Share your edit with others
          </Typography>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AudioDownloader;
